import { CustomValidator, UiSchema } from '@rjsf/utils';
import { CreatePythonFunctionRequest, Project } from 'api/model';
import { getProjectsOptions } from 'api/workflows-steps/formSchema';
import ListSelectionWidget from 'components/form/ListSelectionWidget';
import CodeEditorWidget from 'components/form/CodeEditorWidget';
import FolderPathWidget from 'components/form/FolderPathWidget';
import { JSONSchema7 } from 'json-schema';
import { PYTHON_FUNCTION_INITIAL_VALUE } from 'consts/codeEditorInitialValues';
import { validateFolderPath } from 'utils/folderPath';

const required = ['name', 'function_code'];

function getFormSchema(projects?: Project[], projectId?: string): JSONSchema7 {
  const projectsOptions = getProjectsOptions(projects);

  return {
    type: 'object',
    properties: {
      name: {
        title: 'Name',
        type: 'string',
      },
      description: {
        title: 'Description',
        type: 'string',
      },
      project_id: {
        title: 'Project',
        type: ['string', 'null'],
        description: 'The project this function belongs to. If not set, the function will be global.',
        default: projectId ?? null,
        anyOf: [{ const: null }, ...projectsOptions],
      },
      folder_path: {
        title: 'Folder',
        type: ['string', 'null'],
        description: 'Organize the function into a folder path, e.g. utils/text.',
      },
      extra_libs: {
        type: 'array',
        title: 'Extra libraries',
        description: 'List of extra libraries to be installed, e.g. numpy, transformers==5.3.0',
        items: {
          type: 'string',
          title: 'library',
        },
      },
      function_code: {
        title: 'Function code',
        type: 'string',
      },
    },
    required,
  };
}

function getUiSchema(folders?: string[]): UiSchema {
  return {
    project_id: {
      'ui:widget': ListSelectionWidget,
      'ui:options': {
        autocomplete: true,
      },
    },
    folder_path: {
      'ui:widget': FolderPathWidget,
      'ui:options': {
        folders,
      },
    },
    function_code: {
      'ui:widget': CodeEditorWidget,
      'ui:options': {
        language: 'python',
        initialValue: PYTHON_FUNCTION_INITIAL_VALUE,
      },
    },
  };
}

const validator: CustomValidator<CreatePythonFunctionRequest> = (formData, errors) => {
  const folderError = validateFolderPath(formData?.folder_path);
  if (folderError) {
    errors.folder_path?.addError(folderError);
  }
  return errors;
};

export { getFormSchema, getUiSchema, validator };
